import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/module-container'
import {Box, Text, useSlotRecipe} from '@chakra-ui/react3'
import awardsSlotRecipe, {MODULE_CONTAINER_MAP} from './index.recipe'
import Image from '../../components/cms/image'
import DisImage from '../../components/dis-image'
import Heading from '../../components/cms/heading'
import TypewrighterAnimation from '../../components/cms/typewriterAnimation'
import {hasValidImageSrc, hasValidString} from '../../utils/utils'

const BACKGROUND_WIDTHS = {base: '100vw'}

const Awards = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            headingLevel,
            heading,
            copy,
            copySize,
            highlightColorMode,
            highlightColor,
            background,
            backgroundImageMobile,
            backgroundImageDesktop,
            images,
            spacing
        },
        ref
    ) => {
        // fall back to the other breakpoint's asset so a single configured image covers both
        const mobileBackground = hasValidImageSrc(backgroundImageMobile)
            ? backgroundImageMobile
            : backgroundImageDesktop
        const desktopBackground = hasValidImageSrc(backgroundImageDesktop)
            ? backgroundImageDesktop
            : backgroundImageMobile
        const hasBackground = Boolean(hasValidImageSrc(desktopBackground))

        const recipe = useSlotRecipe({recipe: awardsSlotRecipe})
        // a blank CMS value (vs. undefined) must still fall through to the recipe default
        const styles = useMemo(
            () =>
                recipe({
                    copySize: hasValidString(copySize) ? copySize : undefined,
                    hasBackground
                }),
            [recipe, copySize, hasBackground]
        )
        const containerProps =
            MODULE_CONTAINER_MAP[`${hasBackground}|${spacing}`] ||
            MODULE_CONTAINER_MAP[`${hasBackground}|undefined`]

        const isCustomHighlight = highlightColorMode === 'custom' && hasValidString(highlightColor)

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                css={styles.root}
                {...containerProps}
                ref={ref}
                // the content column sizes against the container, not the page max-width
                layerStyle={null}
            >
                {/* no accessible role here, so tests key off the testid */}
                {hasBackground && (
                    <Box css={styles.backgroundLayer} data-testid="awards-background">
                        <DisImage
                            src={desktopBackground.disBaseLink || desktopBackground.src}
                            alt={desktopBackground.alt || ''}
                            widths={BACKGROUND_WIDTHS}
                            // the recipe owns the sizing, so drop the full-size defaults
                            width={null}
                            height={null}
                            css={styles.backgroundImage}
                            // a native <picture> lets the browser fetch only the matching source
                            {...(mobileBackground.src !== desktopBackground.src && {
                                sources: [
                                    {
                                        src: mobileBackground.disBaseLink || mobileBackground.src,
                                        breakpoint: 'base'
                                    },
                                    {
                                        src: desktopBackground.disBaseLink || desktopBackground.src,
                                        breakpoint: 's'
                                    }
                                ]
                            })}
                        />
                    </Box>
                )}
                <Box css={styles.content}>
                    <Heading
                        {...heading}
                        variant={
                            heading.variant === 'default' || !heading.variant
                                ? 'awards'
                                : heading.variant
                        }
                        headingLevel={headingLevel}
                        alignment="center"
                    ></Heading>
                    <TypewrighterAnimation
                        // default animation starts secondary and turns primary; custom highlight starts primary and turns into the selected hex
                        from={isCustomHighlight ? 'text.primary' : 'text.secondary'}
                        to={isCustomHighlight ? highlightColor : 'text.primary'}
                        css={styles.copy}
                    >
                        {copy.text}
                    </TypewrighterAnimation>
                    {images?.length > 0 && (
                        <Box css={styles.awardsGroup}>
                            {images.map((el, index) => (
                                <Box key={index} css={styles.award}>
                                    <Image
                                        {...el}
                                        {...styles.awardImage}
                                        widths={{
                                            base: '108px',
                                            s: '139px'
                                        }}
                                    ></Image>
                                    <Text css={styles.awardText}>{el.title}</Text>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    Awards.displayName = 'Awards'
}

Awards.propTypes = {
    anchor: PropTypes.string,
    headingLevel: PropTypes.number,
    heading: PropTypes.object,
    copy: PropTypes.object,
    copySize: PropTypes.oneOf(Object.keys(awardsSlotRecipe.variants.copySize)),
    highlightColorMode: PropTypes.oneOf(['default', 'custom']),
    highlightColor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    backgroundImageMobile: PropTypes.object,
    backgroundImageDesktop: PropTypes.object,
    images: PropTypes.array,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom'])
}

export default Awards
